import { useState } from 'react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui';
import { ChevronDown, Close } from '@carbon/icons-react';
import './bulk-action-bar-style.scss';

export interface BulkAction {
  id: string;
  label: string;
}

interface BulkActionBarProps {
  // The bulk actions the user can choose from. The first is selected by
  // default; when only one is supplied the dropdown still renders (per the
  // spec) but simply re-selects the same action.
  actions: BulkAction[];
  selectedCount: number;
  isApplying: boolean;
  // Called with the id of the action chosen in the dropdown when Apply is
  // clicked, so the page can dispatch on it.
  onApply: (actionId: string) => void;
  // Clears the current selection.
  onClear: () => void;
  // When set (and nothing is currently selected), the bar shows the result of
  // the last bulk action inline instead of the selection controls, with an
  // optional Undo and a dismiss button.
  resultMessage?: string | null;
  onUndo?: () => void;
  onDismissResult?: () => void;
  isUndoing?: boolean;
}

export function BulkActionBar({
  actions,
  selectedCount,
  isApplying,
  onApply,
  onClear,
  resultMessage,
  onUndo,
  onDismissResult,
  isUndoing = false,
}: BulkActionBarProps) {
  const [selectedActionId, setSelectedActionId] = useState(actions[0]?.id);
  const selectedAction =
    actions.find((action) => action.id === selectedActionId) ?? actions[0];

  if (!selectedAction) return null;

  // Pins to the very top of the viewport as the page scrolls. The app nav
  // header is `position: relative` (its #header id rule overrides the .header
  // sticky rule), so it scrolls away — the bar must stick at top-0, otherwise a
  // gap opens above it and rows scroll through it. Opaque bg + shadow so rows
  // pass cleanly underneath; px-6 matches the table card's 24px padding so the
  // "N selected" text lines up with the "Showing results …" text below.
  const containerClasses =
    'sticky top-0 z-40 mb-6 flex items-center justify-between gap-4 rounded-md border border-neutral-medium-1 bg-white px-6 py-3 shadow-md';

  // After an action runs the selection is cleared, so when there's a pending
  // result and nothing selected, surface it inline in the same bar.
  const showResult = selectedCount === 0 && !!resultMessage;

  if (showResult) {
    return (
      <div className={containerClasses}>
        <span className="text-sm font-medium">{resultMessage}</span>
        <div className="flex items-center gap-2">
          {onUndo && (
            <Button
              variant="outline-primary"
              size="small"
              onClick={onUndo}
              disabled={isUndoing}
            >
              Undo
            </Button>
          )}
          {onDismissResult && (
            <button
              type="button"
              aria-label="Dismiss"
              onClick={onDismissResult}
            >
              <Close size={16} />
            </button>
          )}
        </div>
      </div>
    );
  }

  const hasSelection = selectedCount > 0;

  return (
    <div className={containerClasses}>
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium">{selectedCount} selected</span>
        <button
          type="button"
          onClick={onClear}
          disabled={!hasSelection || isApplying}
          className="text-sm font-medium text-brand-dark-3 transition-colors hover:text-brand-dark-4 disabled:cursor-not-allowed disabled:text-neutral-medium-3"
        >
          Clear
        </button>
      </div>
      <div className="flex items-center gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger
            disabled={isApplying}
            className="flex min-h-[42px] items-center gap-2 rounded-md border border-neutral-medium-1 bg-white px-3 py-2 text-sm text-neutral-dark-1 transition-colors data-[state=open]:border-brand-medium-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {selectedAction.label}
            <ChevronDown size={12} className="text-brand-dark-3" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {actions.map((action) => (
              <DropdownMenuItem
                key={action.id}
                onClick={() => setSelectedActionId(action.id)}
                disabled={isApplying}
                className={
                  action.id === selectedAction.id
                    ? 'bulk-action-menu-item--selected'
                    : undefined
                }
              >
                {action.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <Button
          variant="basic-primary"
          size="small"
          onClick={() => onApply(selectedAction.id)}
          disabled={!hasSelection || isApplying}
        >
          Apply
        </Button>
      </div>
    </div>
  );
}
